import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  createBuild,
  deleteBuild,
  getBuildByPipelineRunNumber,
  queryBuilds,
  restoreBuild,
  updateBuild,
} from '@/features/builds/data';
import { resolveBuildRunNumber } from '@/features/builds/utils/buildRunNumber';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import {
  gridPaths,
  invalidateBuildDetail,
  invalidateBuildRunDetail,
  invalidateBuilds,
  invalidateGridQuery,
  queryKeys,
  useAccessToken,
  useInvalidateQueries,
} from '@/shared/hooks';
import type { ApiError } from '@/shared/data/client';
import type { BuildDto, BuildStatus, QueryResult } from '@flowqa/shared';

type CreateBuildInput = {
  projectId: string;
  source?: string | null;
  buildKey?: string | null;
  pipelineUrl?: string | null;
  commitSha?: string | null;
  branch?: string | null;
  status?: BuildStatus;
  startedAt?: string | null;
  finishedAt?: string | null;
  tags?: string[];
};

type UpdateBuildInput = {
  id: string;
  source?: string | null;
  buildKey?: string | null;
  pipelineUrl?: string | null;
  commitSha?: string | null;
  branch?: string | null;
  status?: BuildStatus;
  startedAt?: string | null;
  finishedAt?: string | null;
  tags?: string[];
};

type BuildActionInput = { id: string; projectId?: string | null };

export const useBuildsQuery = (
  projectId?: string | null,
  options?: { enabled?: boolean },
): UseQueryResult<QueryResult<BuildDto>, ApiError> => {
  const token = useAccessToken();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const orgKey = selectedOrganisationId ?? null;
  const effectiveProjectId = projectId ?? selectedProjectId ?? null;
  const isEnabled = Boolean(token) && (options?.enabled ?? true);
  return useQuery<QueryResult<BuildDto>, ApiError>({
    queryKey: queryKeys.builds(effectiveProjectId, orgKey),
    enabled: isEnabled,
    queryFn: async (): Promise<QueryResult<BuildDto>> =>
      await queryBuilds(effectiveProjectId, selectedOrganisationId ?? undefined),
    staleTime: 30_000,
  });
};

export const useBuildDetailQuery = (
  pipelineId: string | null,
  runNumber: number | null,
  projectId?: string | null,
): UseQueryResult<BuildDto | null, ApiError> => {
  const token = useAccessToken();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  return useQuery<BuildDto | null, ApiError>({
    queryKey: queryKeys.buildRunDetail(pipelineId, runNumber, orgKey),
    enabled: Boolean(token && pipelineId && runNumber !== null),
    queryFn: async (): Promise<BuildDto | null> => {
      return await getBuildByPipelineRunNumber(
        pipelineId,
        runNumber,
        projectId ?? null,
        selectedOrganisationId ?? undefined,
      );
    },
    staleTime: 15_000,
  });
};

export const useCreateBuild = (): UseMutationResult<BuildDto, ApiError, CreateBuildInput> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;

  return useMutation<BuildDto, ApiError, CreateBuildInput>({
    mutationFn: async (input) => await createBuild(input, selectedOrganisationId),
    onSuccess: async (created) => {
      queryClient.setQueryData(queryKeys.buildDetail(created.id, orgKey), created);
      const runNumber = resolveBuildRunNumber(created.buildKey);
      if (created.pipelineId && runNumber !== null) {
        queryClient.setQueryData(
          queryKeys.buildRunDetail(created.pipelineId, runNumber, orgKey),
          created,
        );
      }
      await invalidateQueries([
        invalidateBuilds(undefined, orgKey),
        invalidateBuilds(created.projectId, orgKey),
        invalidateGridQuery(gridPaths.builds),
        invalidateBuildRunDetail(created.pipelineId ?? null, runNumber, orgKey),
      ]);
    },
  });
};

export const useUpdateBuild = (): UseMutationResult<BuildDto, ApiError, UpdateBuildInput> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;

  return useMutation<BuildDto, ApiError, UpdateBuildInput>({
    mutationFn: async (input) => await updateBuild(input, selectedOrganisationId),
    onSuccess: async (updated) => {
      queryClient.setQueryData(queryKeys.buildDetail(updated.id, orgKey), updated);
      const runNumber = resolveBuildRunNumber(updated.buildKey);
      if (updated.pipelineId && runNumber !== null) {
        queryClient.setQueryData(
          queryKeys.buildRunDetail(updated.pipelineId, runNumber, orgKey),
          updated,
        );
      }
      await invalidateQueries([
        invalidateBuilds(undefined, orgKey),
        invalidateBuilds(updated.projectId, orgKey),
        invalidateBuildDetail(updated.id, orgKey),
        invalidateGridQuery(gridPaths.builds),
        invalidateBuildRunDetail(updated.pipelineId ?? null, runNumber, orgKey),
      ]);
    },
  });
};

export const useDeleteBuild = (): UseMutationResult<
  { id: string; projectId?: string | null },
  ApiError,
  BuildActionInput
> => {
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;

  return useMutation<{ id: string; projectId?: string | null }, ApiError, BuildActionInput>({
    mutationFn: async (input) => {
      const result = await deleteBuild({ id: input.id }, selectedOrganisationId);
      return { ...result, projectId: input.projectId };
    },
    onSuccess: async (deleted) => {
      await invalidateQueries([
        invalidateBuilds(undefined, orgKey),
        ...(deleted.projectId ? [invalidateBuilds(deleted.projectId, orgKey)] : []),
        invalidateBuildDetail(deleted.id, orgKey),
        invalidateGridQuery(gridPaths.builds),
      ]);
    },
  });
};

export const useRestoreBuild = (): UseMutationResult<
  { id: string; projectId?: string | null },
  ApiError,
  BuildActionInput
> => {
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;

  return useMutation<{ id: string; projectId?: string | null }, ApiError, BuildActionInput>({
    mutationFn: async (input) => {
      const result = await restoreBuild({ id: input.id }, selectedOrganisationId);
      return { ...result, projectId: input.projectId };
    },
    onSuccess: async (restored) => {
      await invalidateQueries([
        invalidateBuilds(undefined, orgKey),
        ...(restored.projectId ? [invalidateBuilds(restored.projectId, orgKey)] : []),
        invalidateBuildDetail(restored.id, orgKey),
        invalidateGridQuery(gridPaths.builds),
      ]);
    },
  });
};
